/**
 * The selfcare User Profile form, driven by the same ccp properties and field labels as the
 * back office contact and address forms (contact.* and address.* in tenantFieldConfig).
 *
 * Entries use the customer form shape { fieldName, labelFilter }, so tenant labels,
 * hidden/optional fields, FieldOrder, dropdown options and region lists apply as they
 * do in ui-core. Labels and dropdowns come from contact.* and address.* rows only (scopedOnly).
 * Entries marked `optIn` were never on this form: they show only when the tenant lists them
 * in <prefix>OptInFields (contactOptInFields, addressOptInFields, read by ui-core too) and
 * not in <prefix>HiddenFields, so other tenants keep today's form.
 */
import { applyRegionOptions, applyTenantFieldConfig, getCcpList, getCcpValue, keepStoredOption } from './tenantFieldConfig';

export const CONTACT_FIELDS = [
  { fieldName: 'firstName', labelFilter: 'label.firstName' },
  { fieldName: 'middleName', labelFilter: 'label.middleName', optIn: true },
  { fieldName: 'lastName', labelFilter: 'label.lastName' },
  { fieldName: 'identityDocument', labelFilter: 'label.identityDocument', optIn: true },
];

export const ADDRESS_FIELDS = [
  { fieldName: 'street', labelFilter: 'label.street' },
  { fieldName: 'postalCode', labelFilter: 'label.postalCode' },
  { fieldName: 'country', labelFilter: 'label.country' },
  { fieldName: 'state', labelFilter: 'label.state' },
  { fieldName: 'city', labelFilter: 'label.city' },
  { fieldName: 'district', labelFilter: 'label.district', optIn: true },
];

export const selfCareFields = ({ dataField, ccpPropertyList = [], prefix, language, regions = null, data = null }) => {
  const ccp = ccpPropertyList || [];
  const configured = applyTenantFieldConfig({
    dataField,
    ccpPropertyList: ccp,
    prefix,
    language,
    data,
    scopedOnly: true,
  });
  return applyRegionOptions({ dataField: configured, ccpPropertyList: ccp, prefix, regions, data }).map(item =>
    keepStoredOption(item, data)
  );
};

// The non-empty values only: what every tenant without optional fields sends, as before.
const nonEmpty = record =>
  Object.keys(record).reduce((out, key) => (record[key] ? { ...out, [key]: record[key] } : out), {});

/**
 * The selfCareAddress to send. A tenant with addressOptionalFields sends the whole address the
 * form shows, because the input requires street, city, state, country and postal code: a blank
 * field as '' (the engine stores it as '') and a blank country as defaultCountry (the Country
 * scalar rejects ''). Other tenants send the non-empty values, as before.
 */
export const selfCareAddressPayload = (current, ccpPropertyList) => {
  if (!getCcpList(ccpPropertyList, 'addressOptionalFields').length) return nonEmpty(current);
  const payload = Object.keys(current).reduce(
    (out, key) => ({ ...out, [key]: current[key] === undefined || current[key] === null ? '' : current[key] }),
    {}
  );
  if (!payload.country) {
    const country = getCcpValue(ccpPropertyList, 'defaultCountry');
    if (country) payload.country = country;
    else delete payload.country;
  }
  return payload;
};

/**
 * The selfCareContact to send. A field the tenant lists in contactOptionalFields and the form
 * shows is sent as '' when blank, so the engine stores it empty; any other blank is left out,
 * as the mutation serializer always did. Other tenants list nothing and send what they did.
 */
export const selfCareContactPayload = (current, ccpPropertyList) => {
  const optional = getCcpList(ccpPropertyList, 'contactOptionalFields');
  return Object.keys(current).reduce((out, key) => {
    if (current[key]) return { ...out, [key]: current[key] };
    if (optional.indexOf(key) > -1) return { ...out, [key]: '' };
    return out;
  }, {});
};
